/**
 * Featured-collection slider behavior - extracted from sections/featured-collection.liquid
 *
 * Previously this same ~7.7KB block was inlined once per featured-collection
 * section, so a homepage with 12 sections shipped ~93KB of identical JS.
 * Now it ships once, browser-cached.
 *
 * Auto-inits any slider element marked with data-fcs-section-id, and the two
 * document-level click handlers (variant-option click, mobile select-btn click)
 * are attached once globally instead of once per section.
 */
(function () {
  'use strict';

  async function hydrateDeferredProducts(slider) {
    if (!slider.hasAttribute('data-fcs-deferred')) return;

    const sectionId = slider.dataset.fcsSectionId;
    if (!sectionId) return;

    const url = new URL(window.location.href);
    url.searchParams.set('section_id', sectionId);

    const response = await fetch(url.toString(), {
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    });
    if (!response.ok) throw new Error('Unable to load featured collection');

    const html = await response.text();
    const documentFragment = new DOMParser().parseFromString(html, 'text/html');
    const sourceSlider = documentFragment.getElementById('Slider-' + sectionId);
    if (!sourceSlider) throw new Error('Featured collection markup missing');

    const placeholder = slider.querySelector('[data-fcs-deferred-placeholder]');
    const products = document.createDocumentFragment();
    Array.from(sourceSlider.children).forEach(function (child) {
      if (!child.hasAttribute('data-fcs-deferred-placeholder')) {
        products.appendChild(child);
      }
    });

    // The first homepage rail keeps a small SSR subset for crawlable internal
    // links. Replace the complete subset + placeholder with the authoritative
    // isolated-section response so no duplicate cards remain after hydration.
    if (placeholder) slider.replaceChildren(products);
    else slider.appendChild(products);
    slider.removeAttribute('data-fcs-deferred');
  }

  async function initSlider(slider) {
    if (slider.__fcsInit) return;
    slider.__fcsInit = true;

    const sectionId = slider.dataset.fcsSectionId;
    if (!sectionId) return;

    try {
      await hydrateDeferredProducts(slider);
    } catch (error) {
      slider.__fcsInit = false;
      return;
    }

    const sliderComponent = slider.closest('slider-component');
    if (sliderComponent && typeof sliderComponent.resetPages === 'function') {
      sliderComponent.resetPages();
    }

    const sliderContainer = document.querySelector('.collection.section-' + sectionId + '-padding');
    if (!sliderContainer) return;

    let isDragging = false;
    let startX;
    let scrollLeftStart;
    let hasMoved = false;

    // Drag-to-scroll listeners attach to document ONLY while dragging.
    let _onDocMove = null;
    let _onDocUp = null;
    slider.addEventListener('mousedown', function (e) {
      if (e.button !== 0) return;
      isDragging = true;
      hasMoved = false;
      startX = e.pageX;
      scrollLeftStart = slider.scrollLeft;
      slider.style.cursor = 'grabbing';
      slider.style.userSelect = 'none';
      document.body.style.userSelect = 'none';

      _onDocMove = function (ev) {
        if (!isDragging) return;
        const walkX = (ev.pageX - startX) * 1.5;
        if (Math.abs(walkX) > 5) hasMoved = true;
        slider.scrollLeft = scrollLeftStart - walkX;
      };
      _onDocUp = function () {
        if (!isDragging) return;
        isDragging = false;
        slider.style.cursor = 'grab';
        slider.style.userSelect = '';
        document.body.style.userSelect = '';
        setTimeout(function () { hasMoved = false; }, 50);
        document.removeEventListener('mousemove', _onDocMove);
        document.removeEventListener('mouseup', _onDocUp);
        _onDocMove = null;
        _onDocUp = null;
      };
      document.addEventListener('mousemove', _onDocMove, { passive: true });
      document.addEventListener('mouseup', _onDocUp, { passive: true });
    });

    slider.addEventListener('mouseleave', function () {
      if (isDragging) {
        isDragging = false;
        slider.style.cursor = 'grab';
        slider.style.userSelect = '';
        document.body.style.userSelect = '';
      }
    });

    // Prevent clicks if we dragged
    slider.addEventListener('click', function (e) {
      if (hasMoved) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
      }
    }, true);

    slider.querySelectorAll('a').forEach(function (link) {
      link.addEventListener('click', function (e) {
        if (hasMoved) {
          e.preventDefault();
          e.stopPropagation();
        }
      }, true);
    });

    slider.addEventListener('dragstart', function (e) {
      e.preventDefault();
    });

    slider.querySelectorAll('img').forEach(function (img) {
      img.draggable = false;
    });

    slider.style.cursor = 'grab';
  }

  // ── Variant-click → quick-add modal (delegated) ──
  // Originally attached per section, so 12 sections × 2 handlers = 24 document
  // click listeners. Now: one of each, scoped via the [data-fcs-section-id]
  // marker so it only fires inside featured-collection sections.
  function isInsideFeaturedCollection(target) {
    const wrap = target.closest('[class*="-padding"]');
    return !!(wrap && wrap.querySelector('[data-fcs-section-id]'));
  }

  if (!window.__fcsClickAttached) {
    window.__fcsClickAttached = true;

    document.addEventListener('click', function (e) {
      const target = e.target;
      if (!isInsideFeaturedCollection(target)) return;

      const variantLabel = target.closest('label[for*="Option-"]');
      const variantInput = target.closest('input[type="radio"]');
      if (!variantLabel && !variantInput) return;

      const card = target.closest('.card');
      if (!card) return;
      const modalOpener = card.querySelector('modal-opener[data-modal^="#QuickAdd-"]');
      if (!modalOpener) return;
      const modalButton = modalOpener.querySelector('button');
      if (!modalButton) return;
      setTimeout(function () { modalButton.click(); }, 200);
    });

    document.addEventListener('click', function (e) {
      if (window.innerWidth > 767) return;
      const target = e.target;
      const selectBtn = target.closest('.wb_select_btn');
      if (!selectBtn) return;
      if (!isInsideFeaturedCollection(selectBtn)) return;

      const card = selectBtn.closest('.card');
      if (!card) return;
      const modalOpener = card.querySelector('modal-opener[data-modal^="#QuickAdd-"]');
      if (!modalOpener) return;
      const modalButton = modalOpener.querySelector('button');
      if (!modalButton) return;
      e.preventDefault();
      e.stopPropagation();
      setTimeout(function () { modalButton.click(); }, 100);
    });
  }

  // Lazy-init: only initialize a slider when it's near the viewport. A homepage
  // can ship 20+ of these; initializing all of them on DOMContentLoaded was a
  // large single main-thread block (high TBT). IntersectionObserver spreads the
  // work so below-the-fold sliders init only as the user scrolls toward them.
  // initSlider() is idempotent (guards on __fcsInit), so observing is safe.
  function lazyInit(root) {
    const scope = root || document;
    const sliders = scope.querySelectorAll('[data-fcs-section-id]');

    // Old browsers without IntersectionObserver: init everything immediately
    // (correctness over perf - better a slow slider than a broken one).
    if (!('IntersectionObserver' in window)) {
      sliders.forEach(initSlider);
      return;
    }

    const io = new IntersectionObserver(function (entries, obs) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          initSlider(entry.target);
          obs.unobserve(entry.target);
        }
      });
    }, { rootMargin: '100px 0px' });

    sliders.forEach(function (s) { io.observe(s); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { lazyInit(document); });
  } else {
    lazyInit(document);
  }

  // Theme editor: a section reload re-renders the markup; the new slider
  // element won't have __fcsInit, so initSlider will pick it up.
  document.addEventListener('shopify:section:load', function (e) {
    if (e.target) {
      e.target.querySelectorAll('[data-fcs-section-id]').forEach(initSlider);
    }
  });
})();
